import { modeDeLaFeuille } from '@nextilt/commun';
import type { ChangementDHabit, ChangementDHabitDUneBulle, ChangementDeMode, Feuille } from './api/types';

/**
 * L'habit d'un projet changé dans le panneau « Habit » (chantier habit-du-projet, ticket 02), posé
 * avant la réponse du serveur sur une feuille lue : le projet lui-même et chacune de ses étapes,
 * qui portent l'habit de leur projet (le premier maillon de leur chemin). La feuille d'un autre
 * projet, et ce qui n'est pas une feuille (les cartes dépliées, rangées sous la même clé), sont
 * rendues telles quelles. Le serveur reste celui qui dit l'habit : les feuilles se relisent après.
 * Le type et le papier par défaut des fiches (chantier types-de-fiche, modification 06) suivent le
 * même chemin, posés sur `fiches`.
 */
export function feuilleHabillee(feuille: Feuille, projetId: string, changement: ChangementDHabit): Feuille {
  if (!feuille || typeof feuille !== 'object' || !('chemin' in feuille) || !('habit' in feuille)) return feuille;
  if (feuille.chemin[0]?.id !== projetId) return feuille;
  const { couleur, typeParDefaut, papierParDefaut, ...habit } = changement;
  return {
    ...feuille,
    couleur: couleur ?? feuille.couleur,
    habit: { ...feuille.habit, ...habit },
    fiches: {
      type: typeParDefaut ?? feuille.fiches.type,
      papier: papierParDefaut ?? feuille.fiches.papier,
    },
  };
}

/**
 * L'habit d'une bulle changé dans sa fiche ou son menu « ⋯ » (chantier habit-des-bulles,
 * modification 04), posé avant la réponse du serveur sur une feuille lue : la bulle seule, là où
 * la feuille la porte (au centre, parmi les filles, sous le premier tour de la carte), pas tout le
 * projet. Ce qu'elle contient suit sa couleur de lui-même (`couleurDeBranche`, lue à l'écran).
 * Ce qui n'est pas une feuille (les cartes dépliées, rangées sous la même clé) est rendu tel quel.
 */
export function feuilleAvecLaBulleHabillee(
  feuille: Feuille,
  id: string,
  changement: ChangementDHabitDUneBulle
): Feuille {
  if (!feuille || typeof feuille !== 'object' || !('bulle' in feuille) || !('enfants' in feuille)) return feuille;
  const change = <B extends { id: string }>(b: B): B => (b.id === id ? { ...b, ...changement } : b);
  return {
    ...feuille,
    bulle: change(feuille.bulle),
    enfants: feuille.enfants.map(change),
    carte: feuille.carte ? { ...feuille.carte, dessous: feuille.carte.dessous.map(change) } : feuille.carte,
  };
}

/**
 * Le mode d'une bulle choisi dans le panneau « Mode » (chantier modes-de-la-carte, deuxième passe
 * de 01 et 02), posé avant la réponse du serveur sur la feuille de cette bulle : le sien, et celui
 * à appliquer, le sien sinon celui de ses mères. Les feuilles de ses étapes, et ce qui n'est pas une
 * feuille, sont rendues telles quelles : le serveur redit leur mode quand elles se relisent.
 */
export function feuilleAvecLeMode(feuille: Feuille, id: string, { mode }: ChangementDeMode): Feuille {
  if (!feuille || typeof feuille !== 'object' || !('bulle' in feuille) || !('mode' in feuille)) return feuille;
  if (feuille.bulle.id !== id) return feuille;
  // La règle de `commun/`, la même que le serveur : la feuille ne connaît pas ses mères, elle passe leur mode.
  const bulle = { id, parentId: feuille.bulle.parentId, mode };
  const aAppliquer = modeDeLaFeuille(id, (i) => (i === id ? bulle : undefined), feuille.mode.deLaMere);
  return { ...feuille, mode: { ...feuille.mode, sien: mode, aAppliquer } };
}
